import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useGetAllInvoiceQuery } from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import {
  ICompanyDetails,
  ICustomer,
  ICustomerProject,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import {
  ProductType,
  TransferType,
  WorkOrderStatus,
} from "../../../Core/Enum/enum";
import { useParams } from "react-router-dom";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ReactToPrint from "react-to-print";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import placeholderImage from '../../../Images/fav.png';
import moment from 'moment';
import axios from "axios";

const getStatusLabel = (statusCode: number): string => {
  switch (statusCode) {
    case WorkOrderStatus.Paid:
      return "Paid";
    case WorkOrderStatus.Cancel:
      return "Canceled";
    case WorkOrderStatus.Pending:
      return "Pending";
    default:
      return "Unknown Status";
  }
};

const getTransferType = (statusCode: string): string => {
  switch (statusCode) {
    case TransferType.OneWayTransfer:
      return "One Way Transfer";
    case TransferType.RoundTrip:
      return "Round Trip";
    case TransferType.MultiDestination:
      return "Multi Destination";
    default:
      return "Unknown Status";
  }
};

interface IProduct {
  productName: string;
  productDescription: string;
  productImg: String | null;
  numberOfPassengers: number | null;
  transferType: string;
  transferTypeFrom: string;
  transferTypeTo: string;
  transferTypeDescription: string;
  departureDateTime: Dayjs | null;
  returnDateTime: Dayjs | null;
  departureAddress: string;
  destinationAddress: string;
  multiDestination: string;
  quantity: number | null;
  price: number | null;
  tax: number | null;
  taxAmount: number | null;
  total: number;
  taxId: number | null;
  unitPriceWithDiscount: number | null;
  unitTotalWithDiscount: number;
  productType: string;
  productLongDescription: string;
  productShortDescription: string;
}

interface IDiscount {
  value: number | null;
  description: string;
}

interface IQuoteWorkOrder {
  id: number;
  quoteWorkOrderCompanyId: number;
  quoteWorkOrderCustomerId: string;
  quoteWorkOrderCustomerProjectId: string;
  quoteWorkOrderNo: string;
  quoteWorkOrderIssueDate: Dayjs;
  quoteWorkOrderDueDate: Dayjs;
  quoteWorkOrderAmount: number | null;
  quoteWorkOrderStatus: number;
  quoteWorkOrderSale: IProduct[];
  quoteWorkOrderDiscount: string;
  quoteWorkOrderSubTotal: string;
  quoteWorkOrderSaleGrandTotal: string;
  quoteWorkOrderSaleDiscountDescription: IDiscount[];
  customer: ICustomer;
  company: ICompanyDetails;
  project: ICustomerProject;
  quoteWorkOrderCustomerRemark: string;
  quoteWorkOrderCustomerMemo: string;
  quoteWorkOrderSaleDiscount: string;
  quoteWorkOrderSaleSubTotal: string;
  created_by: number;
  created_at: string;
  mainStatus: number;
  invoiceId: number;
  InvoiceNo: string;
  amount_paid: number;
  po_number: string;
    work_order_id: string;
}

interface  Payment {
  id: number;
  transaction_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  payment_mode: string;
  description: string;
  file_path?: string;
  created_at: string;
  updated_at: string;
  date: string;
  is_return: number;
  is_deleted: number;
}


const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const { id: InvoiceId } = useParams<{ id: string }>();
  useEffect(() => {}, [InvoiceId]);
  const componentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [payments, setPayments] = useState<Payment[]>([]);
  const API_URL = process.env.REACT_APP_API_URL;

  const {
    data: workOrderData,
    isLoading: workIsLoading,
    isError: workOrderError,
    refetch: workOrderRefetch,
  } = useGetAllInvoiceQuery({
    searchText: "",
    id: String(InvoiceId),
    quoteWorkOrderCustomerId: "",
    quoteWorkOrderCustomerProjectId: "",
  });

  const WorkOrderDataList = useMemo(() => {
    if (
      workOrderData &&
      Array.isArray(workOrderData.data) &&
      workOrderData.data.length > 0
    ) {
      return workOrderData.data[0] as IQuoteWorkOrder;
    }
    return null;
  }, [workOrderData]);

 

  const formatAddressReadOnly = (
    streetNumber: string,
    streetName: string,
    city: string,
    province: string,
    postalCode: string,
  ) => {
    return `${streetNumber} ${streetName}, ${city}, ${province}, ${postalCode}`;
  };

  const customerFormatAddressReadOnly = (
    streetNumber: string,
    streetName: string,
    city: string,
    province: string,
    postalCode: string,
  ) => {
    return `${streetNumber} ${streetName},\n ${city}, ${province}, ${postalCode}`;
  }

  const projectFormatAddressReadOnly = (
    streetNumber: string,
    streetName: string,
    city: string,
    province: string,
    postalCode: string,
  ) => {
    return `${streetNumber} ${streetName},\n ${city}, ${province}, ${postalCode}`;
  }

  const initialCompanyAddress = formatAddressReadOnly(
    WorkOrderDataList?.company.companyStreetNumber || "",
    WorkOrderDataList?.company.companyStreetName || "",
    WorkOrderDataList?.company.companyCity || "",
    WorkOrderDataList?.company.companyProvince || "",
    WorkOrderDataList?.company.companyPostalCode || "",
  );

  const initialCustomerAddress = customerFormatAddressReadOnly(
    WorkOrderDataList?.customer.customerStreetNumber || "",
    WorkOrderDataList?.customer.customerStreetName || "",
    WorkOrderDataList?.customer.customerCity || "",
    WorkOrderDataList?.customer.customerProvince || "",
    WorkOrderDataList?.customer.customerPostalCode || "",
  );
  const initialProjectAddress = projectFormatAddressReadOnly(
    WorkOrderDataList?.project?.projectStreetNumber || "",
    WorkOrderDataList?.project?.projectStreetName || "",
    WorkOrderDataList?.project?.projectCity || "",
    WorkOrderDataList?.project?.projectProvince || "",
    WorkOrderDataList?.project?.projectPostalCode || "",
  );
  

  const coursings = Array.isArray(WorkOrderDataList?.quoteWorkOrderSale)
    ? WorkOrderDataList!.quoteWorkOrderSale
    : [];

  const discountList = Array.isArray(
    WorkOrderDataList?.quoteWorkOrderSaleDiscountDescription
  )
    ? WorkOrderDataList!.quoteWorkOrderSaleDiscountDescription
    : [];

    useEffect(() => {
      let newTotalPrice = 0;
      let newTotalTax = 0;
    
      coursings.forEach((product) => {
        newTotalPrice += product.price ?? 0; // Sum of product prices
        newTotalTax += product.taxAmount ?? 0; // Sum of tax amounts
      });
    
      setTotalPrice(newTotalPrice); // Update state with new total price
      setTotalTax(newTotalTax); // Update state with new total tax
    }, [coursings]);




     useEffect(() => {
            fetchPayments();
          }, [WorkOrderDataList?.work_order_id]); 
    
       const fetchPayments = () => {
          if (!WorkOrderDataList?.work_order_id) return;
        
          axios.get(`${API_URL}/payments-by-workorder?work_order_id=${WorkOrderDataList?.work_order_id}`)
            .then(response => {
              const sortedPayments = response.data.sort((a: Payment, b: Payment) => {
                return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
              });
        
               const validPayments = sortedPayments.filter((payment: Payment) => payment.is_deleted === 0 && payment.is_return === 0);
              setPayments(validPayments);
    
              //  const refunded = sortedPayments.filter((payment: Payment) => payment.is_return === 1);
              //  setRefunds(refunded);
    
          
            })
            .catch(error => {
              console.error("Error fetching payments:", error);
            
            });
        };
    

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
       <Card className={classes.quotesCardInvoice} style={{marginLeft:'0px', marginRight:'0px'}}>
            <Grid container spacing={1} padding={"2"}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ display: "flex" }}
                >
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIosIcon  />}
                      className={classes.addButton}
                      onClick={() => navigate(-1)}
                    >
                    Back
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12} >
          <div ref={componentRef} >
          <Card  style={{
              
              display: 'flex',
              flexDirection: 'column',
              // minHeight: '297mm',  // Height for A4 paper
              // minWidth: '190mm',   // Width for A4 paper
                  minHeight: '279mm',  // Height for Letter paper
              minWidth: '216mm',   // Width for Letter paper
              position: 'relative',
               marginLeft: '10mm',  // Optional: Set left margin for print view
               marginRight: '10mm', // Optional: Set right margin for print view
              
              // padding :'12px', 
              // padding: '0 12px',

               boxSizing: 'border-box',
              overflow: 'hidden',  // Hide any content that overflows the container
              backgroundColor: theme.palette.cardAdd.bgColor,
              boxShadow: 'none',
             // borderRadius: '8px',
              width: '100%',
              maxWidth:"1920px",   // Default max-width for large screens
              margin: '0 auto',     // Center the content on large screens
              // [theme.breakpoints.down('lg')]: {
              //   maxWidth: '100%',   // For smaller screens (lg and below), use full width
              // },
              // [theme.breakpoints.down('md')]: {
              //   maxWidth: '100%',   // For smaller screens (md and below), use full width
              // },
              // [theme.breakpoints.down('sm')]: {
              //   maxWidth: '100%',   // For smaller screens (sm and below), use full width
              // },
            
            }}
               >
          <Grid item lg={12} md={12} sm={12} xs={12} className={classes.titleGrid}>
         
          <Grid container className={classes.titleGridInvoice} justifyContent={"space-between"}  alignItems="center" style={{ marginBottom: 'auto', borderTop: '20px solid #4B9CD3', paddingRight: '5px'  }}>
          <Grid item lg={3} md={3} sm={3} xs={4} style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center'}}>
    <img 
      src={WorkOrderDataList?.company?.CompanyLogo ? `${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyLogo}` : ""}
      alt="Company Logo" 
      className={classes.invoiceImg} />
  </Grid>

  <Grid item lg={5} md={5} sm={5} xs={5} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
    <Grid container direction="column" paddingRight={2} style={{textAlign:'right'}}>
      <Grid item>
                                               <Typography variant="subtitle1" sx={{ marginBottom: 0, fontSize: {  md: '1.0rem' } }}>
                                                                                               <strong>{WorkOrderDataList?.company.companyName || " "}</strong>
                                                                                             </Typography>
                                                                                           </Grid>
                                                                                           <Grid item>
                                                                                             <Typography variant="subtitle1" sx={{ marginBottom: 0, fontSize: {  md: '0.9rem' } }}>
                                                                                             {WorkOrderDataList?.company.companySlog || " "}
                                                                                             </Typography>
                                                                                           </Grid>
                                                                                           <Grid item>
                                                                                             {initialCompanyAddress.split("\n").map((line, index) => (
                                                                                                 <Typography key={index} variant="subtitle1"  sx={{  marginBottom: 0, fontSize: {  md: '0.9rem' }, whiteSpace: "nowrap" }} >
                                                                                                   {line}
                                                                                                 </Typography>
                                                                                            
                                                                                             ))}
                                                                                           </Grid>
                                                                                           <Grid item>
                                                                                           <Typography variant="subtitle1" sx={{ marginBottom: 0, fontSize: {  md: '0.9rem' } }}>
                                                                                               {`${WorkOrderDataList?.company.companyMobileNumber || ""} | ${WorkOrderDataList?.company.companyEmail || ""}`}
                                                                                             </Typography>
                                                                                           </Grid>
                                                   </Grid>
                                                 </Grid>
                                               </Grid>
                                               </Grid>

          <div style={{ padding: '0 12px' ,}}>

                <Grid item  lg={12} md={12} sm={12} xs={12} className={classes.titleGrid}>
                  <Grid container paddingTop={3} paddingBottom={3} paddingLeft={1} paddingRight={2} justifyContent="space-between">
                  <Grid item lg={12} md={12} sm={12} xs={12} textAlign={"center"} marginBottom={"25px"}>
                 <Typography variant="subtitle1" style={{fontSize:'30px', letterSpacing:'6px'}}> <strong>Invoice</strong></Typography>
                 </Grid>
        
                 <Grid item lg={6} md={6} sm={6} xs={6}>
                      <Grid container spacing={1}>
                        <Grid item lg={12} md={12} sm={12} xs={12}>
                        {WorkOrderDataList?.quoteWorkOrderCustomerProjectId ? (
  <Grid item lg={12} md={12} sm={12} xs={12}>
    <Typography variant="subtitle1">
      <div>
        <strong>{WorkOrderDataList?.project.projectName || ""}</strong>
      </div>
      <div style={{ whiteSpace: 'nowrap' }}>  {initialProjectAddress.split("\n").map((line, index) => (
                                              <div key={index} >
                                                <Typography >
                                                  {line}
                                                </Typography>
                                              </div>
                                            ))}</div>
      <div  style={{ whiteSpace: 'nowrap' }}>
        {`${WorkOrderDataList?.project.projectPhoneNumber || ""} | ${WorkOrderDataList?.project.projectEmail || ""}`}
      </div>
    </Typography>
  </Grid>
) : (
  <Grid item lg={12} md={12} sm={12} xs={12}>
    <Typography variant="subtitle1">
      <div>
        <strong>
        {(
    (WorkOrderDataList?.customer.customerFirstName || "")
      .charAt(0)
      .toUpperCase() + (WorkOrderDataList?.customer.customerFirstName || "").slice(1).toLowerCase()
  ) +
    " " +
    (
      (WorkOrderDataList?.customer.customerLastName || "")
        .charAt(0)
        .toUpperCase() + (WorkOrderDataList?.customer.customerLastName || "").slice(1).toLowerCase()
    )}
        </strong>
      </div>
      <div style={{ whiteSpace: 'nowrap' }}>
      {initialCustomerAddress.split("\n").map((line, index) => (
                                              <div key={index} >
                                                <Typography >
                                                  {line}
                                                </Typography>
                                              </div>
                                            ))}
</div>
      <div  style={{ whiteSpace: 'nowrap' }}>
        {WorkOrderDataList?.customer.customerMobileNum || ""} | {WorkOrderDataList?.customer.customerEmail || ""}  </div>

      
    </Typography>
  </Grid>
)}

                        
                         
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item lg={6} md={6} sm={6} xs={6}>
                        <Grid container textAlign={"right"}>
                  <Grid item lg={7} md={7} sm={7} xs={7}>
    <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
    Invoice No:
    </Typography>
  </Grid>
  <Grid item lg={5} md={5} sm={5} xs={5}>
    <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
      <strong>{WorkOrderDataList?.InvoiceNo || " "}</strong>
    </Typography>
  </Grid>

  {/* PO Number (Only if it exists) */}
  {WorkOrderDataList?.po_number && (
      <>
        <Grid item lg={7} md={7} sm={7} xs={7}>
          <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
            PO No:
          </Typography>
        </Grid>
        <Grid item lg={5} md={5} sm={5} xs={5}>
          <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
          {WorkOrderDataList.po_number}
          </Typography>
        </Grid>
      </>
    )}

<Grid item lg={7} md={7} sm={7} xs={7}>
    <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
      Invoice Date:
    </Typography>
  </Grid>
  <Grid item lg={5} md={5} sm={5} xs={5}>
    <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
      {WorkOrderDataList?.quoteWorkOrderIssueDate
        ? dayjs(WorkOrderDataList.quoteWorkOrderIssueDate).format("D MMM YYYY")
        : ""}
    </Typography>
  </Grid>

  <Grid item lg={7} md={7} sm={7} xs={7}>
    <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
      Due Date:
    </Typography>
  </Grid>
  <Grid item lg={5} md={5} sm={5} xs={5}>
    <Typography variant="subtitle1" sx={{ marginBottom: 0 }}>
      {WorkOrderDataList?.quoteWorkOrderDueDate
        ? dayjs(WorkOrderDataList.quoteWorkOrderDueDate).format("D MMM YYYY")
        : ""}
    </Typography>
  </Grid>
                        
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  className={classes.titleGrid}
                >
                  <Grid container spacing={2}>
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                      <TableContainer>
                        <Table className={classes.table}>
                          <TableHead className={classes.tableHeaderRow}>
                            <TableRow>
                              {/* <TableCell className={classes.tablheader}>
                              <strong>#</strong>
                            </TableCell> */}
                              <TableCell className={classes.tablheader}>
                                <strong>Description</strong>
                              </TableCell>
                              {/* <TableCell
                                className={classes.tablheader}
                                sx={{ textAlign: "right" }}
                              >
                                <strong>Quantity</strong>
                              </TableCell> */}
                              <TableCell
                                className={classes.tablheader}
                                sx={{ textAlign: "right" }}
                              >
                                <strong>Price</strong>
                              </TableCell>
                              {/* <TableCell
                                className={classes.tablheader}
                                sx={{ textAlign: "right" }}
                              >
                                <strong>Tax</strong>
                              
                              </TableCell> */}
                              {/* <TableCell
                                className={classes.tablheader}
                                sx={{ textAlign: "right" }}
                              >
                                <strong>Price</strong>
                              </TableCell> */}
                              {/* <TableCell
                                className={classes.tablheader}
                                sx={{ textAlign: "right" }}
                              >
                                <strong>Total</strong>
                              </TableCell> */}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {workOrderError || coursings.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={8} align="center">
                                  {workOrderError
                                    ? "Error fetching data"
                                    : "No data available"}
                                </TableCell>
                              </TableRow>
                            ) : (
                              coursings
                                .slice(
                                  page * rowsPerPage,
                                  page * rowsPerPage + rowsPerPage
                                )
                                .map((product, index) => (
                                  <TableRow
                                    key={index}
                                    className={classes.tableRowHover}
                                  >
                                    {/* <TableCell className={classes.tablCell}>
                                    {page * rowsPerPage + index + 1}
                                  </TableCell> */}
                                    <TableCell className={classes.tablCell}>
                                      {!isMobile ? (
                                        <Grid container spacing={1}>
                                          <Grid
                                            item
                                            sm={10}
                                            xs={12}
                                            textAlign={"left"}
                                            alignContent={"center"}
                                          >
                                            <Typography>
                                              <strong>
                                                {product.productName}
                                              </strong>
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      ) : (
                                        <Grid container spacing={1}>
                                          <Grid item textAlign={"left"} alignContent={"center"}
                                          >
                                            <Typography>
                                              <strong>
                                                {product.productName}
                                              </strong>
                                            </Typography>
                                          </Grid>
                                        </Grid>
                                      )}

                                      <Typography variant="body2" color="textSecondary">
                                              {product.productDescription || ""}
                                            </Typography>
                                    </TableCell>
                                    {/* <TableCell
                                      className={classes.tablCell}
                                      align="right"
                                    >
                                      {product.quantity}
                                    </TableCell> */}
                                    <TableCell
                                      className={classes.tablCell}
                                      align="right"
                                    >
                                      {(product.price ?? 0).toFixed(2)}
                                    </TableCell>
                                    {/* <TableCell
                                      className={classes.tablCell}
                                      align="right"
                                    >
                                      {(product.taxAmount && !isNaN(product.taxAmount) ? product.taxAmount.toFixed(2) : "0.00")}
                                    </TableCell> */}
                                    {/* <TableCell
                                      className={classes.tablCell}
                                      align="right"
                                    >
                                      {(
                                        product.unitPriceWithDiscount ?? 0
                                      ).toFixed(2)}
                                    </TableCell> */}
                                    {/* <TableCell
                                      className={classes.tablCell}
                                      align="right"
                                    >
                                      {product.unitTotalWithDiscount != null
                                        ? product.unitTotalWithDiscount.toFixed(
                                            2
                                          )
                                        : "0.00"}
                                    </TableCell> */}
                                  </TableRow>
                                ))
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>


                    <Box display="flex" justifyContent="flex-end" width="100%" marginTop={3} padding={2}>
  <Box width="250px" ml="auto"> 
    <Grid container spacing={1}>

    <Grid item lg={12} md={12} sm={12} xs={12}>
         <Grid container spacing={2}>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography sx={{ whiteSpace: "nowrap" }}>
               Sub Total:
             </Typography>
           </Grid>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography>
          ${totalPrice?.toFixed(2)}
        </Typography>
      </Grid>
    </Grid>
  </Grid>

                    {discountList.map((discount, index) => (
                      <Grid item lg={12} md={12} sm={12} xs={12} key={index}>
                         <Grid container spacing={2}>
                                   <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
                         
                            <Typography>
                              {discount.description || "Discount"}
                              {" :"}
                            </Typography>
                          </Grid>
                          <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
                            <Typography>
                              {"$"}
                              {(discount.value !== null
                                ? discount.value
                                : 0
                              ).toFixed(2)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </Grid>
                    ))}
                 {WorkOrderDataList?.quoteWorkOrderDiscount && parseFloat(WorkOrderDataList.quoteWorkOrderDiscount) !== 0 && (
                    <Grid item lg={12} md={12} sm={12} xs={12}>
                       <Grid container spacing={2}>
                                 
                       <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
                                    <Typography sx={{ whiteSpace: "nowrap" }}>
                                      Total Discount:
                                    </Typography>
                                  </Grid>
                                  <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
                                    <Typography sx={{ whiteSpace: "nowrap" }}>
                            {"$"}
                            {parseFloat(
                              WorkOrderDataList?.quoteWorkOrderDiscount || "0"
                            ).toFixed(2)}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    )}

<Grid item lg={12} md={12} sm={12} xs={12}>
         <Grid container spacing={2}>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography sx={{ whiteSpace: "nowrap" }}>HST:</Typography>
           </Grid>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography>
          ${totalTax?.toFixed(2)}
        </Typography>
      </Grid>
    </Grid>
  </Grid>

  {/* Grand Total */}
  <Grid item lg={12} md={12} sm={12} xs={12}>
         <Grid container spacing={2}>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography sx={{ whiteSpace: "nowrap", fontWeight: "bold" }}>
               Grand Total:
             </Typography>
           </Grid>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography style={{ fontWeight: "bold" }}>
          ${parseFloat(WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "0").toFixed(2)}
        </Typography>
      </Grid>
    </Grid>
  </Grid>

                      {payments.length > 0 &&
    payments.map((payment) => (
      <Grid item lg={12} md={12} sm={12} xs={12} key={payment.id}>
        <Grid container spacing={2}>
          
          <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
            <Typography sx={{ whiteSpace: "nowrap" }}>
              {moment(payment.payment_date).format("DD-MMM-YY")} ({payment.payment_method}):
            </Typography>
          </Grid>
  
          <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
            <Typography>
              ${Number(payment.amount).toFixed(2)}
            </Typography>
          </Grid>
  
        </Grid>
      </Grid>
    ))
  }


  {/* <Grid item lg={12} md={12} sm={12} xs={12}>
         <Grid container spacing={2}>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography sx={{ whiteSpace: "nowrap"}}>
            Settled Amount:
             </Typography>
           </Grid>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
           <Typography>
           ${Number(WorkOrderDataList?.amount_paid ?? 0.00).toFixed(2)}
</Typography>

                </Grid>
              </Grid>
            </Grid> */}

            <Grid item lg={12} md={12} sm={12} xs={12}>
         <Grid container spacing={2}>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography sx={{ whiteSpace: "nowrap"}}>
            Balance Amount:
             </Typography>
           </Grid>
           <Grid item lg={6} md={6} sm={6} xs={6} textAlign="right">
             <Typography >
             ${Math.max(0, parseFloat(WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "0") - parseFloat(String(WorkOrderDataList?.amount_paid || "0"))).toFixed(2)}
         </Typography>
                </Grid>
              </Grid>
            </Grid>

            </Grid>
        </Box>
      </Box>

                    {WorkOrderDataList?.company.CompanyWatermark && (
                      <Grid
                      item
                      lg={12}
                      md={12}
                      sm={12}
                      xs={12}
                      container
                      justifyContent="center"
                      alignItems="center"
                      sx={{
                        position: "absolute", 
                        overflow:"hidden",
                        top: "80%", // Center vertically
                        left: "15%", // Center horizontally
                        transform: "translate(-50%, -50%)",
                        width: "100%", // Cover the entire container width
                        height: "100%", // Cover the entire container height
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 0, // Ensure it's behind the content
                        pointerEvents: "none", // Prevent interaction
                        pageBreakInside: "avoid",
                    
                        '@media print': {
                          position: "fixed", 
                        },
                      }}
                      
                    
                      
                      >
                        <img
                          src={`${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyWatermark}`}
                          alt="Watermark"
                          style={{
                            maxWidth: "80%", // Limits the width for proper scaling
                          
                          }}
                        />
                      </Grid>
                    )}


                    {WorkOrderDataList?.quoteWorkOrderCustomerMemo && WorkOrderDataList.quoteWorkOrderCustomerMemo.trim() !== "" && (
  <Grid item lg={12} md={12} sm={12} xs={12} mt={2}>
    <Typography variant="body2" align="left" style={{ marginLeft: "16px" , minWidth: "400px" }}>
      Note: {WorkOrderDataList.quoteWorkOrderCustomerMemo}
    </Typography>
  </Grid>
)}
                  </Grid>
                </Grid>
                
                </div>
              <Grid container padding={3} 
              style={{ marginTop: 'auto', borderBottom: '20px solid #4B9CD3' }}  >
    <Grid item lg={12} md={12} sm={12} xs={12}>
      <Typography variant="body2" align="center">
      {WorkOrderDataList?.company.companyFooterDetails && (
                             <>
                               {WorkOrderDataList?.company.companyFooterDetails.split('\n').map((line, index) => (
                                 <span key={index}>
                                   {line}
                                   <br />
                                 </span>
                               ))}
                             </>
                           )}
                                                {WorkOrderDataList?.company.companyHstNo && (
                                       <>
                                         HST No: ( {WorkOrderDataList?.company.companyHstNo} ) <br />
                                       </>
                                     )}
      Thank you for choosing our services!
      </Typography>
    </Grid>
  </Grid>

  </Card>
</div>

        

          
        </Grid>




       




        <Grid item lg={12} md={12} sm={12} xs={12}>
         
            <Grid container spacing={1} padding={"40px"}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ display: "flex" }}
                >
                  <Grid item>
                    <ReactToPrint
                      trigger={() => (
                        <Button
                          variant="contained"
                          startIcon={<LocalPrintshopSharpIcon />}
                          className={classes.addButton}
                        >
                          Print
                        </Button>
                      )}
                      content={() => componentRef.current}
                      documentTitle={WorkOrderDataList?.InvoiceNo || "Invoice"}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
